import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AppShell } from '@/components/AppShell';
import { Card, CardBody } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { showToast } from '@/components/ui/Toast';
import { logQueueService } from '@/services/logQueue';
import { gpsService } from '@/services/gps';
import { assignmentRepo } from '@/data/repositories/assignmentRepo';
import { haptics } from '@/lib/haptics';
import { cn } from '@/lib/utils';
import { buildLog, CaptureFields, DispatchSwitch } from '@/types/domain';
import { groupStops, type Stop } from '@/lib/grouping';
import { stopLogLocation } from '@/lib/logLocation';
import { usePickupStore } from '@/stores/pickupStore';
import { useDeliveryStore } from '@/stores/deliveryStore';

const PICKUP_REASONS = ['Closed', 'No Freight', 'Not Ready'] as const;
const DELIVERY_REASONS = ['Refused', 'Closed', 'Undeliverable'] as const;

async function findStop(kind: 'pickup' | 'delivery', idOrKey: string): Promise<Stop | undefined> {
  // Delivery dead calls must search switch 2 (ready to deliver) AND 5
  // (unloading in progress): START UNLOADING moves the bill to 5, so a
  // driver who taps CANCEL UNLOADING and then dead-calls would otherwise
  // never match a stop and CONFIRM stays disabled.
  const items =
    kind === 'pickup'
      ? await assignmentRepo.getLoadItems()
      : await assignmentRepo.getActiveAssignments();
  const stops = [...groupStops(items, kind, false), ...groupStops(items, kind, true)];
  return stops.find((s) => s.key === idOrKey || s.bills.some((b) => b.remF04Id === idOrKey));
}

export function DeadCallPage({ kind }: { kind: 'pickup' | 'delivery' }) {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const reasons = kind === 'pickup' ? PICKUP_REASONS : DELIVERY_REASONS;
  const [reason, setReason] = useState<string | null>(null);
  const [stop, setStop] = useState<Stop | undefined>();
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    setReason(null);
  }, [reasons]);

  useEffect(() => {
    if (id) void findStop(kind, id).then(setStop);
  }, [kind, id]);

  const submit = async () => {
    if (!stop || !reason) return;
    setSubmitting(true);
    haptics.medium();
    try {
      const loc = stopLogLocation(stop);
      const fix = gpsService.peekFix();
      for (const b of stop.bills) {
        await logQueueService.enqueue({
          logType:
            kind === 'pickup'
              ? buildLog.deadCallPickup(loc, reason)
              : buildLog.deadCallDelivery(loc, reason),
          display: 'Display',
          assignmentId: b.remF04Id,
          tag: b.remF02Tag ?? undefined,
          latitude: fix?.latitude,
          longitude: fix?.longitude,
          accuracy: fix?.accuracy,
          additionalFields: { [CaptureFields.switch999]: '999' },
        });
        if (kind === 'delivery') {
          await assignmentRepo.updateDispatchType(b.remF04Id, DispatchSwitch.ShipperToDock);
        }
        await assignmentRepo.markComplete(b.remF04Id);
      }
      await logQueueService.flush();
      if (kind === 'pickup') usePickupStore.getState().reset();
      else useDeliveryStore.getState().reset();
      showToast(`Dead call logged: ${reason}`, 'error');
      navigate('/main', { replace: true });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AppShell title={kind === 'pickup' ? 'Dead Call \u2014 Pickup' : 'Dead Call \u2014 Delivery'} showBack>
      <Card>
        <CardBody>
          <div className="text-xs font-bold uppercase text-brand-muted">Location</div>
          <div className="mt-1 text-base">
            {stop ? stopLogLocation(stop) || '--' : '--'}
          </div>
          {stop && (
            <div className="mt-1 text-xs text-brand-muted">
              {stop.bills.length} bill{stop.bills.length === 1 ? '' : 's'}
            </div>
          )}
        </CardBody>
      </Card>

      <div className="mt-4 text-base font-bold">Select Reason</div>
      <div className="mt-2 space-y-2">
        {reasons.map((r) => (
          <Button
            key={r}
            size="lg"
            fullWidth
            variant={reason === r ? 'danger' : 'outline'}
            className={cn(reason === r && 'ring-2 ring-brand-error')}
            onClick={() => setReason(r)}
          >
            {r.toUpperCase()}
          </Button>
        ))}
      </div>

      <div className="mt-6 space-y-2">
        <Button variant="ghost" size="lg" fullWidth disabled={submitting} onClick={() => navigate(-1)}>
          CANCEL DEAD CALL
        </Button>
        <Button
          variant="danger"
          size="xl"
          fullWidth
          disabled={!reason || !stop || submitting}
          onClick={() => void submit()}
        >
          {submitting ? 'Submitting...' : 'CONFIRM DEAD CALL'}
        </Button>
      </div>
    </AppShell>
  );
}
